import { useContext, useMemo, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

import Colon from '@/components/Colon';
import { LayoutContext } from '@/layouts/baseLayout/provider';
import { ProductItemContext } from '@/providers/productItem';
import { ProductListContext } from '@/providers/productList';
import useProductStore from '@/store/product';
import { env } from '@/utils/baseUrl';
import { buriedPoint } from '@/utils/buriedPoint';
import Utils from '@/utils/Utils';

// 福利信息
const rebateTypeMap = {
  purchase_money: '送采购晶',
  point: '赠送积分',
};

export function WelfareTags(props) {
  const { productWelfareList } = props;
  const { resourceData } = useContext(LayoutContext);

  const welfareMap = useMemo(() => {
    const map = {};
    productWelfareList.forEach((item) => {
      if (map[item.rebateType]) {
        map[item.rebateType].push(item);
      } else {
        map[item.rebateType] = [item];
      }
    });
    return map;
  }, [productWelfareList]);

  if (Object.keys(welfareMap)?.length === 0) {
    return <></>;
  }

  return Object.keys(welfareMap).map((key) => {
    const list = welfareMap[key];
    const content = (
      <div className="p-[10px] text-[12px]">
        {list.map((welfare, index) => {
          return (
            <div className="mb-[10px] last-of-type:mb-0" key={index}>
              {index === 0 && (
                <h4>
                  {welfare?.rebateUserType === 'plus' && <span>PLUS专享</span>}
                  <span>
                    活动规则
                    <Colon />
                  </span>
                </h4>
              )}
              <ul>
                {welfare?.thresholdRedisVOList.map((item, i) => (
                  <li key={`${index}_${i}`}>
                    {welfare.rebateDimension === 'multiple_order_amount' ? '累计 ' : '单笔 '}
                    {welfare.rebateDimension === 'single_order_amount' ||
                    (welfare.rebateDimension === 'multiple_order_amount' && item.limitPartakeCount !== 1)
                      ? '每 '
                      : ''}
                    满{item.orderAmount}
                    <Colon />送<span className="text-[#FF4960]">{item.rebateAmount}</span>
                  </li>
                ))}
              </ul>
              <a
                className="text-[rgba(0,147,230)]"
                href={`${welfare.activityDetailUrl || resourceData?.projectConfigVO?.lcscActivityUrl + '/member/welfare/zone.html'}`}
                target="_blank"
              >
                查看具体规则 》
              </a>
            </div>
          );
        })}
      </div>
    );
    return (
      <Popover
        key={key}
        placement="bottom"
        content={content}
        overlayClassName="lcsc-popover-wrapper"
        zIndex={2}
        align={{
          offset: [0, -5],
        }}
      >
        <span className="mb-[5px] mr-[6px] inline-block h-[18px] cursor-default rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[18px] text-[#22A0E8]">
          {rebateTypeMap[key]}
        </span>
      </Popover>
    );
  });
}

// 满额返券
export function ReturnCoupon({ returnCouponDesc }) {
  if (returnCouponDesc?.length > 0) {
    const items = returnCouponDesc.map((item, index) => (
      <li key={index} className="mt-[7px] leading-[18px]">
        <span className="mr-[5px]">{item.message}</span>
        {item.url && (
          <a data-spm="mf" className="text-[#0093e6]" href={item.url} target="_blank">
            详情&gt;&gt;
          </a>
        )}
      </li>
    ));

    const content = <ul className="p-[3px_10px_10px] text-[12px] text-[#666]">{items}</ul>;

    return (
      <Popover
        placement="bottomLeft"
        content={content}
        overlayClassName="lcsc-popover-wrapper"
        zIndex={2}
        align={{
          offset: [0, -5],
        }}
      >
        <span className="mb-[5px] mr-[6px] inline-block h-[18px] cursor-default rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[18px] text-[#22A0E8]">
          满额返券
        </span>
      </Popover>
    );
  }
  return;
}

// 返采购晶
export function ReturnRebate({ totalPrices, purchaseCount }) {
  const { rebate, isMerge, productRebateList, productVO } = useContext(ProductItemContext);
  const { resourceData } = useContext(LayoutContext);

  const rebateMoney = useMemo(() => {
    let reallyRebate = 1;
    for (let i = 0; i < productRebateList.length; i += 1) {
      if (
        purchaseCount >= productRebateList[i].spNumber * productVO?.theRatio &&
        (purchaseCount <= productRebateList[i].epNumber * productVO?.theRatio || productRebateList[i].epNumber === -1)
      ) {
        reallyRebate = productRebateList[i].rebate;
      }
    }
    return Math.round((1 - reallyRebate) * totalPrices * 100) / 100;
  }, [totalPrices, productRebateList, productVO]);

  const content = (
    <div className="p-[10px_10px_15px_10px] text-[12px]">
      <p>
        按<span className="text-[#de3231]">购买数量</span>
        进行返采购晶
        <Colon />
      </p>
      <ul className="text-center">
        {isMerge ? (
          <li>
            第1
            {productRebateList.length > 1 && `至第${productRebateList.length}`}
            阶梯
            <Colon />返<span className="text-[#de3231]">{rebate}%</span>
          </li>
        ) : (
          productRebateList.map((item, index) => (
            <li key={index}>
              {item?.spNumber * productVO?.theRatio}+<Colon />返
              <span className="text-[#de3231]">{item?.rebateCopy}%</span>
            </li>
          ))
        )}
      </ul>
      <p>
        当前购买现货商品，发货后预计返采购晶
        <span className="text-[#de3231]">￥{rebateMoney}</span>，
      </p>
      <p>采购晶可抵扣现货商品金额及兑换礼品</p>
      <a
        data-spm="dh"
        className="text-[#0093e6]"
        href={`${resourceData?.projectConfigVO?.lcscActivityUrl}/point/use/info.html?from=dh`}
        target="_blank"
      >
        兑换专区
      </a>
    </div>
  );

  return (
    <Popover
      placement="bottomLeft"
      content={content}
      overlayClassName="lcsc-popover-wrapper"
      zIndex={2}
      align={{
        offset: [0, -5],
      }}
    >
      <span className="mb-[5px] mr-[6px] inline-block h-[18px] cursor-default rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[18px] text-[#22A0E8]">
        返{rebate}%
      </span>
    </Popover>
  );
}

// 优惠券
export function CouponTicket(props) {
  const { type, text, brandId, provider, productId, isMro, productType } = props;
  const { searchResult } = useContext(ProductListContext);
  const { index, productVO, rs_log } = useContext(ProductItemContext);
  const { updateCouponData } = useProductStore();
  const [hover, setHover] = useState(false);

  function clickCoupon() {
    buriedPoint({
      op: 'get_coupon',
      data: {
        pc: productVO?.productId,
        model: productVO?.productModel || productVO?.productCodeManufacturer,
        brand: productVO?.brandName || productVO?.productGradePlateName,
        code: productVO?.productCode,
        kw: searchResult?.globalSearchKeyword,
        cp: searchResult?.currePage,
        ix: index,
        rs_log,
      },
    });
    updateCouponData({
      open: true,
      product: {
        brandId,
        provider,
        productId,
        isMro,
        productType,
      },
    });
  }

  return (
    <button
      className="group-1 relative mb-[5px] mr-[6px] inline-flex h-[18px] cursor-pointer items-center overflow-hidden
      rounded-[3px] border-[1px] border-solid border-[#FFD3DA] text-[#FF4960]"
      onClick={clickCoupon}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
    >
      <div className={`${hover ? 'invisible' : ''}`}>
        {type && <span className="inline-block bg-[#FFECEF] px-[3px]">{type}</span>}
        <span className="inline-block px-[6px]">{text}</span>
      </div>
      <div
        className={`absolute bottom-0 left-0 right-0 top-0 bg-[#FFECEF] text-center leading-[16px] ${hover ? 'block' : 'hidden'}`}
      >
        立即领券
      </div>
    </button>
  );
}

// smt链接
export function JlcSmt(props) {
  const { smtStockNumber, productVO } = props;
  const { searchResult } = useContext(ProductListContext);
  const { index, rs_log } = useContext(ProductItemContext);

  const link = useMemo(() => {
    let host = 'https://www.jlcsmt.com/lcsc/detail';
    if (env !== 'pro') {
      host = 'https://test.jlcsmt.com/lcsc/detail';
    }
    return `${host}?componentCode=${productVO.productCode}&stockNumber=10&presaleOrderSource=shop`;
  }, [productVO]);

  function handleClick() {
    buriedPoint({
      op: 'immediately_check',
      data: {
        pc: productVO?.productId,
        brand: productVO?.productGradePlateName,
        model: productVO?.productModel,
        code: productVO?.productCode,
        kw: searchResult?.globalSearchKeyword,
        cp: searchResult?.currePage,
        ix: index,
        rs_log,
      },
    });
  }

  return (
    <a href={link} target="_blank" onClick={handleClick} rel="nofollow">
      <div
        className="mb-[5px] inline-flex h-[20px] rounded-[3px] border-[1px] border-solid border-[#D2E2EB] bg-[#fff] leading-[18px] text-[#1FA0E8] hover:bg-[#F1F7FC] hover:text-[#0294E7]"
        title={`嘉立创库存${Utils.unitConversion(smtStockNumber)}`}
      >
        <span className="rounded-[3px_0_6px_3px] bg-[#F1F7FC] px-[5px]">SMT补贴</span>
        <span className="line-clamp-1 flex-1 break-all px-[5px]">嘉立创库存{Utils.unitConversion(smtStockNumber)}</span>
      </div>
    </a>
  );
}

// 不可用商品券
export function UnavailableProductCoupon({ productVO, isCouponUnavailable }) {
  return (
    isCouponUnavailable &&
    !productVO.selfProductCode && (
      <span className="mb-[5px] inline-flex h-[20px] items-center indent-[5px] text-[12px] text-[#999999]">
        <ExclamationCircleOutlined className="text-[14px]" />
        不可用商品券
      </span>
    )
  );
}

// plus 商品券
export function PlusCouponTicket({
  productVO,
  hasPlusCoupon,
  isCouponUnavailable,
  groupActivityProductVO,
  productType,
}) {
  if (productType === 'lt') {
    return (
      groupActivityProductVO?.hasPlusCoupon &&
      !groupActivityProductVO?.isCouponUnavailable && (
        <CouponTicket text="PLUS专享券" productId={groupActivityProductVO?.productId} productType={productType} />
      )
    );
  }
  return (
    hasPlusCoupon &&
    !isCouponUnavailable && <CouponTicket text="PLUS专享券" productId={productVO?.productId}></CouponTicket>
  );
}

// MRO 商品券
export function MroCoupon({ isShowMroCouponLabel, groupActivityProductVO, productVO, productType }) {
  if (productType === 'lt') {
    return (
      groupActivityProductVO?.isShowMroCouponLabel && (
        <CouponTicket
          text="工业品专享"
          isMro={true}
          productId={groupActivityProductVO?.productId}
          productType={productType}
        ></CouponTicket>
      )
    );
  }
  return (
    isShowMroCouponLabel && (
      <CouponTicket text="工业品专享" isMro={true} productId={productVO?.productId}></CouponTicket>
    )
  );
}

// 展示优惠券
export function ProductCouponTicket({
  productVO,
  couponType,
  isUseConfig,
  couponAmount,
  couponDiscount,
  isFreeExpress,
  couponThresholdMoney,
  groupActivityProductVO,
  isCouponUnavailable,
  productType,
}) {
  let couponInfo = {};
  if (productType === 'lt') {
    couponInfo = groupActivityProductVO?.ltProductCouponVO || {};
    couponInfo.productType = productType;
    couponInfo.productId = groupActivityProductVO?.productId;
    if (groupActivityProductVO?.isCouponUnavailable || !couponInfo.couponType) {
      return <></>;
    }
  } else {
    couponInfo = {
      isUseConfig,
      couponAmount,
      couponDiscount,
      isFreeExpress,
      couponThresholdMoney,
      couponType,
      productId: productVO?.productId,
    };
    // 主营商品
    if (isCouponUnavailable || !couponType) {
      return <></>;
    }
  }
  let text = '';
  let type = '';
  if (['product', 'freight'].includes(couponInfo?.couponType) && productVO?.productUnsealVendition === 'yes') {
    if (couponInfo?.couponType === 'product') {
      type = '商';
      if (couponInfo?.isUseConfig) {
        text = `满${couponInfo?.couponThresholdMoney}-${couponInfo?.couponAmount}`;
      } else {
        text = `最高减${couponInfo?.couponAmount}`;
      }
    } else {
      type = '运';
      if (couponInfo?.isFreeExpress) {
        text = `包邮券`;
      } else {
        text = `${couponInfo?.couponAmount}元运费券`;
      }
    }
  } else if (['discount'].includes(couponInfo?.couponType)) {
    type = '折';
    text = `再享${couponInfo?.couponDiscount / 10}折`;
  }
  return (
    <CouponTicket type={type} text={text} productId={couponInfo?.productId} productType={couponInfo?.productType} />
  );
}

export default function ProductTags(props) {
  const { marketLabelVO } = useContext(ProductItemContext);
  const {
    isMro,
    productWelfareList,
    hasPlusCoupon,
    isCouponUnavailable,
    isShowSmtSubsidyInfo,
    couponType,
    isUseConfig,
    couponAmount,
    couponDiscount,
    isFreeExpress,
    isShowMroCouponLabel,
    smtStockNumber,
    couponThresholdMoney,
    isProductRebate,
    isGuidePrice,
    rebate,
    totalPrices,
    purchaseCount,
    productVO,
    rohsLabal,
    groupActivityProductVO,
    productType,
  } = props;

  // 满额返现
  const returnCouponDom = useMemo(() => {
    return (
      productVO?.isReturnCoupon &&
      !Utils.checkReturnCoupon(productVO?.returnCouponDesc) && (
        <ReturnCoupon returnCouponDesc={productVO?.returnCouponDesc}></ReturnCoupon>
      )
    );
  }, [productVO]);

  // 送积分和送采购晶
  const welfareTagsDom = useMemo(() => {
    const list = (productWelfareList || []).sort((a, b) => {
      if (b.rebateType === 'point' && a.rebateType === 'purchase_money') {
        return -1;
      } else {
        return 0;
      }
    });
    return list?.length > 0 && <WelfareTags productWelfareList={productWelfareList}></WelfareTags>;
  }, [productVO, productWelfareList]);

  // mro
  const mroTagDom = useMemo(() => {
    return (
      isMro && (
        <span className="mb-[5px] mr-[6px] inline-block h-[20px] text-nowrap rounded-[3px] border-[1px] border-solid border-[#D2E2EB] bg-[#fff] px-[8px] leading-[18px] text-[#1FA0E8]">
          工业品
        </span>
      )
    );
  }, [isMro]);

  // 嘉立创贴片
  const jlcSmtDom = useMemo(() => {
    return isShowSmtSubsidyInfo && smtStockNumber > 0 && <JlcSmt {...props} spm="j"></JlcSmt>;
  }, [isShowSmtSubsidyInfo, smtStockNumber]);

  // SMT扩展库
  const smtDom = useMemo(() => {
    return (
      productVO.smtLabel && (
        <span className="mb-[5px] mr-[6px] inline-block h-[20px] text-nowrap rounded-[3px] border-[1px] border-solid border-[#D2E2EB] bg-[#fff] leading-[18px] text-[#1FA0E8]">
          <span className="inline-block rounded-[3px_0_6px_3px] bg-[#F1F7FC] px-[5px]">SMT</span>
          <span className="inline-block px-[5px]">{productVO.smtLabel?.replace('SMT', '')}</span>
        </span>
      )
    );
  }, [productVO]);

  const rohsDom = useMemo(() => {
    return (
      rohsLabal && (
        <span className="px-[5px] mb-[5px] mr-[6px] inline-block h-[20px] text-nowrap rounded-[3px] border-[1px] border-solid border-[#D7EBD2] bg-[#fff] leading-[18px] text-[#3FB54B]">
          {rohsLabal}
        </span>
      )
    );
  }, [rohsLabal]);

  const marketLabelDom = useMemo(() => {
    return (
      marketLabelVO?.labelStr && (
        <p>
          <span className="mb-[5px] mr-[6px] line-clamp-1 inline-flex h-[20px] max-w-[160px] text-nowrap rounded-[3px] bg-[#FFF2E6] px-[8px] font-[450] leading-[20px] text-[#B1865D]">
            {marketLabelVO.labelStr}
          </span>
        </p>
      )
    );
  }, [marketLabelVO]);

  const returnRebateDom = useMemo(() => {
    if (isProductRebate && !isGuidePrice && rebate > 0) {
      return <ReturnRebate totalPrices={totalPrices} purchaseCount={purchaseCount}></ReturnRebate>;
    }
  }, [isProductRebate, isGuidePrice, rebate, totalPrices]);

  return (
    <>
      {marketLabelDom}
      {(rohsLabal || isMro || productVO?.smtLabel) && (
        <p>
          {rohsDom}
          {mroTagDom}
          {smtDom}
        </p>
      )}
      {!productVO?.selfProductCode && (
        <>
          {jlcSmtDom}
          {returnRebateDom}
          {welfareTagsDom}
          {returnCouponDom}
          <MroCoupon
            isShowMroCouponLabel={isShowMroCouponLabel}
            productVO={productVO}
            groupActivityProductVO={groupActivityProductVO}
            productType={productType}
          />
          <ProductCouponTicket
            productVO={productVO}
            groupActivityProductVO={groupActivityProductVO}
            couponType={couponType}
            couponThresholdMoney={couponThresholdMoney}
            isFreeExpress={isFreeExpress}
            isUseConfig={isUseConfig}
            isCouponUnavailable={isCouponUnavailable}
            couponAmount={couponAmount}
            couponDiscount={couponDiscount}
            productType={productType}
          />
          <PlusCouponTicket
            productVO={productVO}
            hasPlusCoupon={hasPlusCoupon}
            isCouponUnavailable={isCouponUnavailable}
            groupActivityProductVO={groupActivityProductVO}
            productType={productType}
          />
          <UnavailableProductCoupon productVO={productVO} isCouponUnavailable={isCouponUnavailable} />
        </>
      )}
      {productVO?.selfProductCode && <TariffTags productVO={productVO?.lcBookProductVO} />}
    </>
  );
}
// 关税
export function TariffTags(props) {
  const { productVO } = props;

  const content = (
    <div className="p-[10px] text-[12px]">
      <p>此商品需额外支付关税，商品总额{productVO?.tariffRate}%</p>
      {(productVO?.showTariffList || []).map((item, index) => {
        return (
          <p key={index} className="text-[#949FAB]">
            {item.tariffName}
            <Colon />
            {item.rate}%
          </p>
        );
      })}
    </div>
  );

  return (
    productVO?.isTariff === 1 &&
    productVO?.tariffRate > 0 && (
      <Popover
        content={content}
        placement="bottom"
        overlayClassName="lcsc-popover-wrapper"
        zIndex={2}
        align={{
          offset: [0, -5],
        }}
      >
        <span className="mb-[5px] mr-[6px] inline-block h-[18px] rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[18px] text-[#22A0E8]">
          关税
        </span>
      </Popover>
    )
  );
}
// 商检
export function CommercialInspectionTags(props) {
  const { productVO } = props;

  const content = (
    <div className="p-[10px] text-[12px]">此商品需额外支付商检费，费用：{productVO?.commercialInspectionCharge}</div>
  );

  return (
    productVO?.isCommercialInspection === 1 &&
    productVO?.commercialInspectionCharge > 0 && (
      <Popover
        content={content}
        placement="bottom"
        overlayClassName="lcsc-popover-wrapper"
        zIndex={2}
        align={{
          offset: [0, -5],
        }}
      >
        <span className="mb-[5px] mr-[6px] inline-block h-[18px] rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[18px] text-[#22A0E8]">
          商检
        </span>
      </Popover>
    )
  );
}
// 3C
export function ThreeCTags(props) {
  const { productVO } = props;

  const content = (
    <div className="p-[10px] text-[12px]">此商品需额外支付3C认证费用，费用：{productVO?.certification3cFee}</div>
  );

  return (
    productVO?.is3c === 1 &&
    productVO?.certification3cFee > 0 && (
      <Popover
        content={content}
        placement="bottom"
        overlayClassName="lcsc-popover-wrapper"
        align={{
          offset: [0, -5],
        }}
      >
        <span className="mb-[5px] mr-[6px] inline-block h-[18px] rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[18px] text-[#22A0E8]">
          3C
        </span>
      </Popover>
    )
  );
}

export function OverseasProductTags(props) {
  const { productVO, dhAndTeAndRoCoupon, osCoupon } = props;
  // 展示优惠券
  const couponTicketDom = useMemo(() => {
    let coupon;
    if (Utils.isLcBook(productVO?.provider)) {
      coupon = dhAndTeAndRoCoupon?.[productVO?.productCode];
    } else {
      coupon = osCoupon?.[`${productVO?.provider}_${productVO?.brandId}`];
    }
    if (!coupon?.couponType || coupon?.isCouponUnavailable) {
      return <></>;
    }

    let text = '';
    let type = '';
    if (['product', 'freight'].includes(coupon?.couponType)) {
      if (coupon?.couponType === 'product') {
        type = '商';
        if (coupon?.isUseConfig) {
          text = `满${coupon?.couponThresholdMoney}-${coupon?.couponAmount}`;
        } else {
          text = `最高减${coupon?.couponAmount}`;
        }
      } else {
        type = '运';
        if (coupon?.isFreeExpress) {
          text = `包邮券`;
        } else {
          text = `${coupon?.couponAmount}元运费券`;
        }
      }
    } else if (['discount'].includes(coupon?.couponType)) {
      type = '折';
      text = `再享${coupon?.couponDiscount / 10}折`;
    }
    return (
      <CouponTicket
        type={type}
        text={text}
        provider={productVO?.provider}
        brandId={productVO?.brandId}
        productId={productVO?.productId || coupon?.productId}
      ></CouponTicket>
    );
  }, [productVO, dhAndTeAndRoCoupon, osCoupon]);

  const plusCouponTicketDom = useMemo(() => {
    let coupon;
    if (Utils.isLcBook(productVO?.provider)) {
      coupon = dhAndTeAndRoCoupon?.[productVO?.productCode];
    } else {
      coupon = osCoupon?.[`${productVO?.provider}_${productVO?.brandId}`];
    }
    if (coupon?.isCouponUnavailable) {
      return <></>;
    }
    return (
      coupon?.hasPlusCoupon && (
        <CouponTicket
          text="PLUS专享券"
          provider={productVO?.provider}
          brandId={productVO?.brandId}
          productId={productVO?.productId || coupon?.productId}
        ></CouponTicket>
      )
    );
  }, [productVO, dhAndTeAndRoCoupon, osCoupon]);

  const customTagDom = useMemo(() => {
    return (
      productVO?.tag && (
        <span className="mb-[5px] mr-[6px] line-clamp-1 inline-flex h-[20px] max-w-[160px] rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[20px] text-[#22A0E8]">
          {productVO.tag}
        </span>
      )
    );
  }, [productVO]);

  const deliveryRateDom = useMemo(() => {
    return (
      productVO?.deliveryRate && (
        <span className="mb-[5px] mr-[6px] line-clamp-1 inline-flex h-[20px] max-w-[160px] text-nowrap rounded-[3px] bg-[#F1F7FC] px-[8px] leading-[20px] text-[#22A0E8]">
          近1个月准时交付率{productVO.deliveryRate}%
        </span>
      )
    );
  }, [productVO]);

  const unavailableProductCoupon = useMemo(() => {
    let coupon;
    if (Utils.isLcBook(productVO?.provider)) {
      coupon = dhAndTeAndRoCoupon?.[productVO?.productCode];
    } else {
      coupon = osCoupon?.[`${productVO?.provider}_${productVO?.brandId}`];
    }
    if (coupon?.isCouponUnavailable) {
      return (
        <span className="mb-[5px] inline-flex h-[20px] items-center indent-[5px] text-[12px] text-[#999999]">
          <ExclamationCircleOutlined className="text-[14px]" />
          不可用商品券
        </span>
      );
    }
    return <></>;
  }, [productVO, dhAndTeAndRoCoupon, osCoupon]);

  return (
    <>
      <TariffTags productVO={productVO} />
      <CommercialInspectionTags productVO={productVO} />
      <ThreeCTags productVO={productVO} />
      {customTagDom}
      {deliveryRateDom}
      {couponTicketDom}
      {plusCouponTicketDom}
      {unavailableProductCoupon}
    </>
  );
}
